import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SelectChangeEvent } from "@mui/material/Select";
import { FormTextField } from "../../atoms/FormTextField/FormTextField";
import { FormSelect } from "../../atoms/FormSelect/FormSelect";
import { FormCheckboxGroup } from "../../atoms/FormCheckboxGroup/FormCheckboxGroup";
import { SuburbAutocompleteMui } from "../../molecules/SuburbAutocomplete/SuburbAutocomplete";
import { PriceRangeSlider } from "../../atoms/PriceRangeSlider";
import { EmailRecipientsInput } from "../../atoms/EmailRecipientsInput";

export interface ContactFormData {
  name: string;
  email: string;
  emailRecipients?: string[];
  suburb: string;
  suburbSecondary?: string;
  priceMin?: number | null;
  priceMax?: number | null;
  propertyTypes?: string[];
  reportFrequency: "weekly" | "fortnightly" | "monthly";
  sendDay: number; // 0=Sun, 1=Mon, ..., 6=Sat
  isActive: boolean;
}

export interface ContactFormProps {
  contact?: Partial<ContactFormData> & {
    id: string;
    unsubscribedAt?: string | null;
  };
  onSubmit: (data: ContactFormData) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string;
}

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
];

const SEND_DAY_OPTIONS = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "House", label: "House" },
  { value: "Townhouse", label: "Townhouse" },
  { value: "Apartment", label: "Apartment" },
  { value: "Unit", label: "Unit" },
  { value: "Section", label: "Section" },
  { value: "Lifestyle", label: "Lifestyle" },
  { value: "Rural", label: "Rural" },
];

export function ContactForm({
  contact,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}: ContactFormProps) {
  const isEditing = Boolean(contact);
  const [propertyTypesError, setPropertyTypesError] = useState<string | null>(
    null,
  );

  // Note: Parent should use key={contact?.id} to reset form when contact changes
  const [formData, setFormData] = useState<ContactFormData>({
    name: contact?.name ?? "",
    email: contact?.email ?? "",
    emailRecipients: contact?.emailRecipients ?? [contact?.email ?? ""],
    suburb: contact?.suburb ?? "",
    suburbSecondary: contact?.suburbSecondary ?? "",
    priceMin: contact?.priceMin ?? null,
    priceMax: contact?.priceMax ?? null,
    propertyTypes:
      contact?.propertyTypes ?? PROPERTY_TYPE_OPTIONS.map((o) => o.value),
    reportFrequency: contact?.reportFrequency ?? "weekly",
    sendDay: contact?.sendDay ?? 3, // Default to Wednesday
    isActive: contact?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate property types - at least one required
    if (!formData.propertyTypes || formData.propertyTypes.length === 0) {
      setPropertyTypesError("Please select at least one property type");
      return;
    }

    // Filter out empty email recipients
    const cleanedData = {
      ...formData,
      emailRecipients: formData.emailRecipients?.filter((e) => e.trim()) ?? [],
      // Sync primary email with first recipient
      email: formData.emailRecipients?.[0]?.trim() || formData.email,
    };
    onSubmit(cleanedData);
  };

  const handleChange =
    (field: keyof ContactFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  const handleFrequencyChange = (e: SelectChangeEvent<string>) => {
    setFormData({
      ...formData,
      reportFrequency: e.target.value as "weekly" | "fortnightly" | "monthly",
    });
  };

  const handleSendDayChange = (e: SelectChangeEvent<string>) => {
    setFormData({
      ...formData,
      sendDay: parseInt(e.target.value, 10),
    });
  };

  const handleSuburbChange = (value: string) => {
    setFormData({ ...formData, suburb: value });
  };

  const handleSuburbSecondaryChange = (value: string) => {
    setFormData({ ...formData, suburbSecondary: value });
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  const handleEmailRecipientsChange = (emails: string[]) => {
    setFormData({
      ...formData,
      emailRecipients: emails,
      // Keep primary email in sync with first recipient
      email: emails[0] || formData.email,
    });
  };

  const handlePriceRangeChange = (min: number | null, max: number | null) => {
    setFormData({ ...formData, priceMin: min, priceMax: max });
  };

  const handlePropertyTypesChange = (types: string[]) => {
    setFormData({ ...formData, propertyTypes: types });
    // Clear error when user selects a type
    if (types.length > 0 && propertyTypesError) {
      setPropertyTypesError(null);
    }
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      return isEditing ? "Saving..." : "Creating...";
    }
    return isEditing ? "Save Changes" : "Create Contact";
  };

  const hasAdvancedFilters = Boolean(
    formData.suburbSecondary ||
    formData.priceMin !== null ||
    formData.priceMax !== null ||
    (formData.propertyTypes && formData.propertyTypes.length > 0),
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      <FormTextField
        label="Name"
        value={formData.name}
        onChange={handleChange("name")}
        required
      />

      <EmailRecipientsInput
        value={formData.emailRecipients ?? [formData.email]}
        onChange={handleEmailRecipientsChange}
      />

      <Divider />

      <Typography variant="subtitle1" fontWeight={600}>
        Location
      </Typography>

      <SuburbAutocompleteMui
        label="Primary Suburb"
        value={formData.suburb}
        onChange={handleSuburbChange}
      />

      <SuburbAutocompleteMui
        label="Secondary Suburb (Optional)"
        value={formData.suburbSecondary ?? ""}
        onChange={handleSuburbSecondaryChange}
      />

      <Divider />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormSelect
          label="Report Frequency"
          value={formData.reportFrequency}
          onChange={handleFrequencyChange}
          options={FREQUENCY_OPTIONS}
        />

        <FormSelect
          label="Send Day"
          value={String(formData.sendDay)}
          onChange={handleSendDayChange}
          options={SEND_DAY_OPTIONS}
        />
      </Box>

      {contact?.unsubscribedAt ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          This contact unsubscribed on{" "}
          {new Date(contact.unsubscribedAt).toLocaleDateString("en-NZ")} and
          cannot be reactivated.
        </Alert>
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isActive}
              onChange={handleActiveChange}
            />
          }
          label="Active"
        />
      )}

      <Accordion defaultExpanded={hasAdvancedFilters}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            Advanced Filters
            {hasAdvancedFilters && (
              <Typography
                component="span"
                color="primary"
                sx={{ ml: 1, fontSize: "0.875rem" }}
              >
                (Active)
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PriceRangeSlider
              minValue={formData.priceMin ?? null}
              maxValue={formData.priceMax ?? null}
              onChange={handlePriceRangeChange}
            />

            <Divider />

            <FormCheckboxGroup
              label="Property Types"
              options={PROPERTY_TYPE_OPTIONS}
              value={formData.propertyTypes ?? []}
              onChange={handlePropertyTypesChange}
              error={Boolean(propertyTypesError)}
              helperText={propertyTypesError ?? undefined}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mt: 2,
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{ flex: { sm: "none" } }}
        >
          {getSubmitButtonText()}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            sx={{ flex: { sm: "none" } }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
